import { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  HelpCircle,
  Users,
  Building2,
  FileText,
  CheckCircle2,
  XCircle,
  FolderKanban,
  Target,
  Clock,
  Star,
  Settings,
  User,
  LogIn,
  Plus,
  BarChart3,
  AlertTriangle,
  BookOpen,
  Trophy,
  Flag,
  Mail,
  Bell,
  Globe,
  TrendingUp,
  Cog
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const DocumentationPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(user?.role || 'consultant');

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8 fade-in" data-testid="documentation-page">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center p-4 bg-orange-100 rounded-full">
            <BookOpen className="w-10 h-10 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Guide Utilisateur
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Bienvenue dans la documentation de la plateforme de suivi des interventions CCI Togo. 
            Sélectionnez votre profil pour consulter le guide adapté.
          </p>
        </div>

        {/* Role Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="consultant" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Consultant
            </TabsTrigger>
            <TabsTrigger value="sme" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              PME
            </TabsTrigger>
            <TabsTrigger value="admin" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Administrateur
            </TabsTrigger>
          </TabsList>

          {/* ==================== CONSULTANT GUIDE ==================== */}
          <TabsContent value="consultant" className="space-y-6">
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <User className="w-5 h-5" />
                  Guide du Consultant
                </CardTitle>
                <CardDescription className="text-blue-700">
                  Apprenez à gérer vos interventions, suivre votre progression et finaliser vos missions
                </CardDescription>
              </CardHeader>
            </Card>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {/* Connexion */}
              <AccordionItem value="connexion" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <LogIn className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">Connexion à la plateforme</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <ol className="list-decimal list-inside space-y-3">
                    <li>Accédez à la page de connexion via l&apos;URL fournie par votre administrateur</li>
                    <li>Entrez votre <strong>email</strong> et votre <strong>mot de passe</strong></li>
                    <li>Cliquez sur <strong>&quot;Se connecter&quot;</strong></li>
                    <li>Vous serez redirigé vers votre tableau de bord personnel</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <HelpCircle className="w-4 h-4 inline mr-2" />
                    Si vous avez oublié votre mot de passe, cliquez sur <strong>&quot;Mot de passe oublié ?&quot;</strong> sur la page de connexion pour recevoir un lien de réinitialisation par email.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Créer une intervention */}
              <AccordionItem value="intervention" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Plus className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">Créer une intervention</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <ol className="list-decimal list-inside space-y-3">
                    <li>Cliquez sur <strong>&quot;Nouvelle intervention&quot;</strong> depuis votre tableau de bord</li>
                    <li>Sélectionnez l&apos;<strong>affectation</strong> (Projet + PME) concernée</li>
                    <li>Choisissez le <strong>type d&apos;intervention</strong> (Formation, Audit, Conseil, etc.)</li>
                    <li>Sélectionnez la <strong>date</strong> de l&apos;intervention</li>
                    <li>Indiquez la <strong>durée</strong> en heures</li>
                    <li>Rédigez une <strong>description détaillée</strong> des activités réalisées</li>
                    <li>Ajoutez des <strong>pièces jointes</strong> si nécessaire (max 10 Mo par fichier)</li>
                    <li>Cliquez sur <strong>&quot;Créer l&apos;intervention&quot;</strong></li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <Bell className="w-4 h-4 inline mr-2" />
                    La PME recevra une <strong>notification par email</strong> pour valider l&apos;intervention.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Suivi progression */}
              <AccordionItem value="suivi" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <BarChart3 className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="font-medium">Suivre sa progression</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">Votre tableau de bord affiche :</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>PME attribuées</strong> : Liste des entreprises dont vous êtes responsable</li>
                    <li><strong>Total interventions</strong> : Nombre total d&apos;interventions créées</li>
                    <li><strong>En attente</strong> : Interventions non encore validées par les PME</li>
                    <li><strong>Heures totales</strong> : Cumul des heures d&apos;intervention validées</li>
                  </ul>
                  <p className="mt-4">Dans la section <strong>&quot;Mes interventions&quot;</strong>, vous pouvez :</p>
                  <ul className="list-disc list-inside space-y-2 mt-2">
                    <li>Voir l&apos;historique complet de vos interventions</li>
                    <li>Filtrer par statut (En attente, Validée, Rejetée)</li>
                    <li>Modifier une intervention non encore validée</li>
                    <li>Consulter les retours des PME (note, commentaires)</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Indice de digitalisation */}
              <AccordionItem value="digitalisation" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="font-medium">Indice de digitalisation</span>
                    <Badge className="bg-indigo-100 text-indigo-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    L&apos;indice de digitalisation mesure le niveau de transformation numérique d&apos;une PME sur une échelle de <strong>0 à 100</strong>.
                  </p>
                  <h4 className="font-semibold mb-3">Comment renseigner l&apos;indice :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Accédez à <strong>&quot;Entreprises&quot;</strong> dans le menu</li>
                    <li>Cliquez sur le bouton <strong>&quot;Indice&quot;</strong> à côté de la PME</li>
                    <li>Sélectionnez le type : <strong>Initial</strong> (début de mission) ou <strong>Final</strong> (fin de mission)</li>
                    <li>Entrez la valeur de l&apos;indice (0-100)</li>
                    <li>Cliquez sur <strong>&quot;Enregistrer&quot;</strong></li>
                  </ol>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
                      <strong>Indice Initial</strong><br/>
                      À renseigner au début de votre accompagnement
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 text-sm">
                      <strong>Indice Final</strong><br/>
                      À renseigner après l&apos;indice initial, en fin de mission
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Signaler une PME */}
              <AccordionItem value="signalement" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Flag className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="font-medium">Signaler un problème avec une PME</span>
                    <Badge className="bg-orange-100 text-orange-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Si vous rencontrez des difficultés avec une entreprise, vous pouvez la signaler aux administrateurs.
                  </p>
                  <h4 className="font-semibold mb-3">Types de signalement :</h4>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li><strong>Injoignable</strong> : Impossible de contacter la PME</li>
                    <li><strong>Faillite</strong> : L&apos;entreprise est en cessation d&apos;activité</li>
                    <li><strong>Déménagement</strong> : L&apos;entreprise a changé d&apos;adresse</li>
                    <li><strong>Fermeture</strong> : L&apos;entreprise a fermé définitivement</li>
                    <li><strong>Autre</strong> : Tout autre problème</li>
                  </ul>
                  <h4 className="font-semibold mb-3">Comment signaler :</h4>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Accédez à <strong>&quot;Entreprises&quot;</strong></li>
                    <li>Cliquez sur <strong>&quot;Signaler&quot;</strong> à côté de la PME concernée</li>
                    <li>Sélectionnez le type de problème</li>
                    <li>Décrivez la situation en détail</li>
                    <li>Cliquez sur <strong>&quot;Envoyer&quot;</strong></li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <Bell className="w-4 h-4 inline mr-2" />
                    Les administrateurs seront notifiés par email et pourront traiter votre signalement.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Rapport final */}
              <AccordionItem value="rapport-final" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Trophy className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">Soumettre un rapport final</span>
                    <Badge className="bg-emerald-100 text-emerald-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Lorsque vous avez atteint les objectifs de votre mission (heures et interventions validées), vous pouvez soumettre un rapport final.
                  </p>
                  <h4 className="font-semibold mb-3">Conditions requises :</h4>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li>Avoir atteint le <strong>nombre minimum d&apos;heures</strong> validées</li>
                    <li>Avoir atteint le <strong>nombre minimum d&apos;interventions</strong> validées</li>
                  </ul>
                  <h4 className="font-semibold mb-3">Comment soumettre :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Accédez à <strong>&quot;Entreprises&quot;</strong></li>
                    <li>Cliquez sur <strong>&quot;Progression&quot;</strong> ou <strong>&quot;Soumettre Rapport&quot;</strong></li>
                    <li>Vérifiez que les objectifs sont atteints</li>
                    <li>Remplissez le formulaire :</li>
                  </ol>
                  <div className="ml-6 space-y-2 mb-4">
                    <p>• <strong>Résumé</strong> de la mission</p>
                    <p>• <strong>Objectifs atteints</strong></p>
                    <p>• <strong>Difficultés rencontrées</strong></p>
                    <p>• <strong>Recommandations</strong> pour l&apos;entreprise</p>
                    <p>• <strong>Prochaines étapes</strong> suggérées</p>
                  </div>
                  <ol className="list-decimal list-inside space-y-2" start="5">
                    <li>Cliquez sur <strong>&quot;Soumettre le rapport final&quot;</strong></li>
                  </ol>
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg text-emerald-800 text-sm">
                    <Trophy className="w-4 h-4 inline mr-2" />
                    Une animation de félicitations s&apos;affichera ! La mission sera marquée comme terminée.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Profil */}
              <AccordionItem value="profil" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <User className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">Gérer son profil</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">Accédez à votre profil via le menu utilisateur (en haut à droite) :</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Mon profil</strong> : Modifiez votre nom, email, téléphone et photo</li>
                    <li><strong>Paramètres</strong> : Changez votre mot de passe</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>

          {/* ==================== PME GUIDE ==================== */}
          <TabsContent value="sme" className="space-y-6">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-900">
                  <Building2 className="w-5 h-5" />
                  Guide PME
                </CardTitle>
                <CardDescription className="text-emerald-700">
                  Apprenez à valider les interventions, donner votre retour et consulter les rapports
                </CardDescription>
              </CardHeader>
            </Card>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {/* Première connexion */}
              <AccordionItem value="connexion-pme" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <LogIn className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">Première connexion</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <ol className="list-decimal list-inside space-y-3">
                    <li>Votre compte a été créé par l&apos;administrateur lors de l&apos;enregistrement de votre entreprise</li>
                    <li>Vous avez reçu un <strong>mot de passe temporaire</strong></li>
                    <li>Connectez-vous avec votre email et ce mot de passe</li>
                    <li>Il est recommandé de <strong>changer votre mot de passe</strong> après la première connexion (Menu → Paramètres)</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <HelpCircle className="w-4 h-4 inline mr-2" />
                    Si vous avez oublié votre mot de passe, cliquez sur <strong>&quot;Mot de passe oublié ?&quot;</strong> pour recevoir un lien de réinitialisation.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Validation */}
              <AccordionItem value="validation" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">Valider une intervention</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <Bell className="w-4 h-4 inline mr-2" />
                    Vous recevez un <strong>email de notification</strong> à chaque nouvelle intervention enregistrée.
                  </div>
                  <ol className="list-decimal list-inside space-y-3">
                    <li>Accédez à <strong>&quot;Validations en attente&quot;</strong> depuis le menu</li>
                    <li>Cliquez sur l&apos;intervention que vous souhaitez examiner</li>
                    <li>Vérifiez les détails : date, durée, description, pièces jointes</li>
                    <li>Remplissez le <strong>formulaire de retour</strong> :</li>
                  </ol>
                  <div className="ml-6 mt-3 space-y-2">
                    <p className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span><strong>Note globale</strong> (1 à 5 étoiles)</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span><strong>Efficacité</strong> de l&apos;intervention</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span><strong>Communication</strong> du consultant</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span><strong>Professionnalisme</strong></span>
                    </p>
                  </div>
                  <ol className="list-decimal list-inside space-y-3 mt-4" start="5">
                    <li>Ajoutez vos commentaires sur les <strong>points forts</strong> et <strong>axes d&apos;amélioration</strong></li>
                    <li>Cliquez sur <strong>&quot;Valider&quot;</strong> ou <strong>&quot;Rejeter&quot;</strong></li>
                  </ol>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 text-sm">
                      <CheckCircle2 className="w-4 h-4 inline mr-2" />
                      <strong>Valider</strong> : L&apos;intervention est confirmée et comptabilisée
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-red-800 text-sm">
                      <XCircle className="w-4 h-4 inline mr-2" />
                      <strong>Rejeter</strong> : L&apos;intervention n&apos;est pas acceptée
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <Bell className="w-4 h-4 inline mr-2" />
                    Le consultant sera <strong>notifié par email</strong> de votre décision avec vos commentaires.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Historique */}
              <AccordionItem value="historique" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="font-medium">Consulter l&apos;historique</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p>La section <strong>&quot;Historique&quot;</strong> vous permet de :</p>
                  <ul className="list-disc list-inside space-y-2 mt-3">
                    <li>Voir toutes les interventions passées pour votre entreprise</li>
                    <li>Consulter les retours que vous avez donnés</li>
                    <li>Suivre l&apos;évolution des prestations de conseil</li>
                    <li>Télécharger les pièces jointes</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Rapport final PME */}
              <AccordionItem value="rapport-final-pme" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Trophy className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">Consulter le rapport final</span>
                    <Badge className="bg-emerald-100 text-emerald-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Lorsque votre consultant termine sa mission, il soumet un rapport final que vous pouvez consulter.
                  </p>
                  <h4 className="font-semibold mb-3">Le rapport contient :</h4>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Résumé</strong> de l&apos;accompagnement</li>
                    <li><strong>Objectifs atteints</strong> durant la mission</li>
                    <li><strong>Difficultés rencontrées</strong></li>
                    <li><strong>Recommandations</strong> pour améliorer votre digitalisation</li>
                    <li><strong>Prochaines étapes</strong> suggérées</li>
                    <li><strong>Statistiques</strong> : heures totales, nombre d&apos;interventions</li>
                  </ul>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <FileText className="w-4 h-4 inline mr-2" />
                    Le rapport est accessible depuis votre tableau de bord une fois la mission terminée.
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>

          {/* ==================== ADMIN GUIDE ==================== */}
          <TabsContent value="admin" className="space-y-6">
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Settings className="w-5 h-5" />
                  Guide Administrateur
                </CardTitle>
                <CardDescription className="text-purple-700">
                  Gérez les utilisateurs, projets, entreprises, et configurez la plateforme
                </CardDescription>
              </CardHeader>
            </Card>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {/* Projets */}
              <AccordionItem value="projets" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <FolderKanban className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="font-medium">Gérer les projets</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <h4 className="font-semibold mb-3">Créer un projet :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Accédez à <strong>&quot;Projets&quot;</strong> dans le menu</li>
                    <li>Cliquez sur <strong>&quot;Nouveau projet&quot;</strong></li>
                    <li>Renseignez le nom, la description, les dates de début et fin</li>
                    <li>Cliquez sur <strong>&quot;Créer&quot;</strong></li>
                  </ol>
                  <h4 className="font-semibold mb-3">Dashboard projet :</h4>
                  <p>Chaque projet dispose d&apos;un tableau de bord affichant :</p>
                  <ul className="list-disc list-inside space-y-2 mt-2">
                    <li>Nombre de consultants et PME affectés</li>
                    <li>Total d&apos;interventions (validées, en attente, rejetées)</li>
                    <li>Progression globale du projet</li>
                    <li>Alertes (retard, faible taux de validation)</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Affectations */}
              <AccordionItem value="affectations" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Target className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="font-medium">Gérer les affectations</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <h4 className="font-semibold mb-3">Créer une affectation :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Depuis le dashboard d&apos;un projet, cliquez sur <strong>&quot;Gérer les affectations&quot;</strong></li>
                    <li>Cliquez sur <strong>&quot;Nouvelle affectation&quot;</strong></li>
                    <li>Sélectionnez le <strong>consultant</strong> et la <strong>PME</strong></li>
                    <li>Définissez les objectifs contractuels :</li>
                  </ol>
                  <div className="ml-6 space-y-2 mb-4">
                    <p>• <strong>Heures minimum</strong> : Nombre d&apos;heures à réaliser</p>
                    <p>• <strong>Interventions minimum</strong> : Nombre d&apos;interventions à effectuer</p>
                    <p>• <strong>Période</strong> : Dates de début et fin autorisées</p>
                  </div>
                  <h4 className="font-semibold mb-3">Import en masse (CSV) :</h4>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Cliquez sur <strong>&quot;Import CSV&quot;</strong></li>
                    <li>Collez vos données au format : <code className="bg-slate-100 px-1 rounded">email;nom_pme;heures;interventions;date_debut;date_fin</code></li>
                    <li>Cliquez sur <strong>&quot;Importer&quot;</strong></li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              {/* Utilisateurs */}
              <AccordionItem value="utilisateurs" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Users className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">Gérer les utilisateurs</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <h4 className="font-semibold mb-3">Créer un utilisateur :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Accédez à <strong>&quot;Utilisateurs&quot;</strong></li>
                    <li>Cliquez sur <strong>&quot;Nouvel utilisateur&quot;</strong></li>
                    <li>Renseignez email, nom, mot de passe et rôle</li>
                    <li>Cliquez sur <strong>&quot;Créer&quot;</strong></li>
                  </ol>
                  <h4 className="font-semibold mb-3">Rôles disponibles :</h4>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li><strong>Admin</strong> : Accès complet à la plateforme</li>
                    <li><strong>Consultant</strong> : Crée des interventions, suit ses missions</li>
                    <li><strong>PME</strong> : Valide les interventions de son entreprise</li>
                    <li><strong>Contrôleur</strong> : Accès en lecture seule aux statistiques</li>
                  </ul>
                  <h4 className="font-semibold mb-3">Actions disponibles :</h4>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Modifier le rôle</strong> d&apos;un utilisateur</li>
                    <li><strong>Réinitialiser le mot de passe</strong></li>
                    <li><strong>Supprimer</strong> un utilisateur</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Entreprises */}
              <AccordionItem value="entreprises" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Building2 className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">Gérer les entreprises</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <h4 className="font-semibold mb-3">Créer une entreprise (PME) :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Accédez à <strong>&quot;Entreprises&quot;</strong></li>
                    <li>Cliquez sur <strong>&quot;Nouvelle entreprise&quot;</strong></li>
                    <li>Renseignez les informations :</li>
                  </ol>
                  <div className="ml-6 space-y-2 mb-4">
                    <p>• Nom, description, adresse</p>
                    <p>• Région, préfecture, commune</p>
                    <p>• Contact : nom, genre, email, téléphone, WhatsApp</p>
                    <p>• Secteur d&apos;activité, nombre d&apos;employés</p>
                    <p>• Géolocalisation (latitude, longitude)</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                    <HelpCircle className="w-4 h-4 inline mr-2" />
                    Un compte utilisateur PME est <strong>automatiquement créé</strong> avec un mot de passe généré. 
                    Ce mot de passe s&apos;affiche une seule fois - notez-le pour le transmettre au contact.
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Signalements */}
              <AccordionItem value="signalements" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="font-medium">Gérer les signalements</span>
                    <Badge className="bg-orange-100 text-orange-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Les consultants peuvent signaler des problèmes avec les PME. Vous êtes notifié par email à chaque nouveau signalement.
                  </p>
                  <h4 className="font-semibold mb-3">Accéder aux signalements :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Cliquez sur <strong>&quot;Signalements&quot;</strong> dans le menu</li>
                    <li>Consultez la liste des signalements avec leurs statuts</li>
                    <li>Filtrez par statut si nécessaire</li>
                  </ol>
                  <h4 className="font-semibold mb-3">Traiter un signalement :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Cliquez sur un signalement pour voir les détails</li>
                    <li>Changez le statut : <strong>Nouveau → En cours → Vérifié → Résolu/Classé</strong></li>
                    <li>Ajoutez des <strong>notes administratives</strong></li>
                    <li>Cliquez sur <strong>&quot;Mettre à jour&quot;</strong></li>
                  </ol>
                  <h4 className="font-semibold mb-3">Statistiques :</h4>
                  <p>Le tableau de bord affiche :</p>
                  <ul className="list-disc list-inside space-y-2 mt-2">
                    <li>Nombre total de signalements</li>
                    <li>Répartition par statut</li>
                    <li>Répartition par type de problème</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Rapports finaux */}
              <AccordionItem value="rapports-finaux" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Trophy className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium">Consulter les rapports finaux</span>
                    <Badge className="bg-emerald-100 text-emerald-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Accédez aux rapports finaux soumis par les consultants lorsqu&apos;ils terminent leurs missions.
                  </p>
                  <h4 className="font-semibold mb-3">Accéder aux rapports :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Cliquez sur <strong>&quot;Rapports Finaux&quot;</strong> dans le menu</li>
                    <li>Consultez les statistiques globales :</li>
                  </ol>
                  <div className="ml-6 space-y-2 mb-4">
                    <p>• Nombre de missions terminées</p>
                    <p>• Heures totales réalisées</p>
                    <p>• Interventions validées</p>
                    <p>• Entreprises accompagnées</p>
                  </div>
                  <ol className="list-decimal list-inside space-y-2" start="3">
                    <li>Recherchez un rapport par consultant, entreprise ou projet</li>
                    <li>Cliquez sur <strong>&quot;Consulter&quot;</strong> pour voir les détails complets</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              {/* Indice de digitalisation */}
              <AccordionItem value="stats-digitalisation" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="font-medium">Statistiques de digitalisation</span>
                    <Badge className="bg-indigo-100 text-indigo-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Suivez l&apos;impact de la transformation digitale des PME accompagnées.
                  </p>
                  <h4 className="font-semibold mb-3">Accéder aux statistiques :</h4>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Cliquez sur <strong>&quot;Digitalisation&quot;</strong> dans le menu</li>
                    <li>Consultez les indicateurs globaux :</li>
                  </ol>
                  <div className="ml-6 space-y-2 mb-4">
                    <p>• <strong>Indice initial moyen</strong> : Niveau de départ des PME</p>
                    <p>• <strong>Indice final moyen</strong> : Niveau après accompagnement</p>
                    <p>• <strong>Progression moyenne</strong> : Amélioration constatée</p>
                  </div>
                  <h4 className="font-semibold mb-3">Analyses disponibles :</h4>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li><strong>Par région</strong> : Maritime, Plateaux, Centrale, Kara, Savanes, DAGL</li>
                    <li><strong>Par secteur</strong> : Agriculture, Commerce, Services, etc.</li>
                    <li><strong>Par genre</strong> : Progression selon le genre du dirigeant</li>
                    <li><strong>Détail PME</strong> : Liste complète avec indices individuels</li>
                  </ul>
                  <h4 className="font-semibold mb-3">Filtres :</h4>
                  <p>Combinez les filtres pour affiner vos analyses (région + secteur + genre).</p>
                </AccordionContent>
              </AccordionItem>

              {/* Configuration */}
              <AccordionItem value="configuration" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Cog className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">Configuration du système</span>
                    <Badge className="bg-slate-100 text-slate-700 ml-2">Nouveau</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-slate-600">
                  <p className="mb-4">
                    Accédez à la page <strong>&quot;Configuration&quot;</strong> pour paramétrer la plateforme.
                  </p>
                  
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                    Onglet &quot;Général&quot;
                  </h4>
                  <ul className="list-disc list-inside space-y-2 mb-4 ml-2">
                    <li><strong>URL de l&apos;application</strong> : Adresse publique utilisée dans les emails</li>
                    <li><strong>Nom de l&apos;application</strong> : Affiché dans les emails</li>
                  </ul>

                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    Onglet &quot;Email (SMTP)&quot;
                  </h4>
                  <p className="mb-2">Configurez le serveur d&apos;envoi d&apos;emails :</p>
                  <ul className="list-disc list-inside space-y-2 mb-4 ml-2">
                    <li><strong>Serveur SMTP</strong> : Adresse du serveur (ex: smtp.gmail.com)</li>
                    <li><strong>Port</strong> : 587 (TLS) ou 465 (SSL)</li>
                    <li><strong>Chiffrement</strong> : TLS, SSL ou Aucun</li>
                    <li><strong>Utilisateur/Mot de passe</strong> : Identifiants du compte email</li>
                    <li><strong>Email expéditeur</strong> : Adresse d&apos;envoi</li>
                    <li><strong>Nom expéditeur</strong> : Nom affiché (ex: &quot;CCI Togo&quot;)</li>
                  </ul>
                  <div className="p-3 bg-blue-50 rounded-lg text-blue-800 text-sm mb-4">
                    <HelpCircle className="w-4 h-4 inline mr-2" />
                    Utilisez le bouton <strong>&quot;Tester l&apos;envoi&quot;</strong> pour vérifier que la configuration fonctionne.
                  </div>

                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-orange-600" />
                    Onglet &quot;Notifications&quot;
                  </h4>
                  <p className="mb-2">Activez/désactivez les notifications automatiques :</p>
                  <ul className="list-disc list-inside space-y-2 ml-2">
                    <li><strong>Nouvelle intervention</strong> : Notifier la PME</li>
                    <li><strong>Validation/Rejet</strong> : Notifier le consultant</li>
                    <li><strong>Nouveau signalement</strong> : Notifier les administrateurs</li>
                    <li><strong>Mission terminée</strong> : Notifier le consultant</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
        </Tabs>

        {/* FAQ Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-slate-600" />
              Questions fréquentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger>Comment récupérer mon mot de passe ?</AccordionTrigger>
                <AccordionContent>
                  Cliquez sur <strong>&quot;Mot de passe oublié ?&quot;</strong> sur la page de connexion. 
                  Entrez votre email et vous recevrez un lien de réinitialisation valide pendant 1 heure.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-2">
                <AccordionTrigger>Puis-je modifier une intervention déjà validée ?</AccordionTrigger>
                <AccordionContent>
                  Non, une fois qu&apos;une intervention est validée ou rejetée par la PME, elle ne peut plus être modifiée. 
                  En cas d&apos;erreur, contactez votre administrateur.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-3">
                <AccordionTrigger>Comment sont calculées les progressions ?</AccordionTrigger>
                <AccordionContent>
                  La progression est calculée uniquement sur les interventions <strong>validées</strong> par les PME. 
                  Les heures validées sont comparées aux objectifs définis dans l&apos;affectation.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-4">
                <AccordionTrigger>Quand puis-je soumettre un rapport final ?</AccordionTrigger>
                <AccordionContent>
                  Vous pouvez soumettre un rapport final lorsque les deux conditions suivantes sont remplies :
                  <ul className="list-disc list-inside mt-2">
                    <li>Nombre d&apos;heures validées ≥ objectif minimum</li>
                    <li>Nombre d&apos;interventions validées ≥ objectif minimum</li>
                  </ul>
                  Le bouton &quot;Soumettre Rapport&quot; apparaîtra automatiquement.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-5">
                <AccordionTrigger>Je ne reçois pas les notifications par email</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Vérifiez vos spams/courriers indésirables</li>
                    <li>Assurez-vous que votre email est correct dans votre profil</li>
                    <li>Contactez l&apos;administrateur pour vérifier que les notifications sont activées et que le SMTP est configuré</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="faq-6">
                <AccordionTrigger>Que signifie l&apos;indice de digitalisation ?</AccordionTrigger>
                <AccordionContent>
                  L&apos;indice de digitalisation mesure le niveau de transformation numérique d&apos;une entreprise sur une échelle de 0 à 100 :
                  <ul className="list-disc list-inside mt-2">
                    <li><strong>0-25</strong> : Faible digitalisation</li>
                    <li><strong>26-50</strong> : Digitalisation débutante</li>
                    <li><strong>51-75</strong> : Bonne digitalisation</li>
                    <li><strong>76-100</strong> : Digitalisation avancée</li>
                  </ul>
                  La différence entre l&apos;indice final et initial mesure l&apos;impact de l&apos;accompagnement.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Contact Support */}
        <div className="text-center py-8 border-t border-slate-200">
          <p className="text-slate-500">
            Besoin d&apos;aide supplémentaire ? Contactez l&apos;équipe CCI Togo
          </p>
        </div>
      </div>
    </Layout>
  );
};
